import type { EnvelopeStatus, FieldType, RecipientStatus } from "./types";

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
export function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  if (Math.abs(mins) < 60) return mins === 0 ? "just now" : rtf.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return rtf.format(days, "day");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
export const fullDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const shortDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export const STATUS: Record<EnvelopeStatus, { label: string; tone: "muted" | "accent" | "good" | "warn" | "bad" }> = {
  draft: { label: "Draft", tone: "muted" },
  sent: { label: "Waiting for others", tone: "accent" },
  completing: { label: "Finishing", tone: "accent" },
  completed: { label: "Completed", tone: "good" },
  declined: { label: "Declined", tone: "bad" },
  voided: { label: "Voided", tone: "warn" },
  expired: { label: "Expired", tone: "warn" },
};

export const RECIPIENT_STATUS: Record<RecipientStatus, string> = {
  pending: "Not sent yet",
  notified: "Email sent",
  viewed: "Viewed",
  signed: "Signed",
  declined: "Declined",
};

export const FIELD_LABEL: Record<FieldType, string> = {
  signature: "Signature",
  initials: "Initials",
  dateSigned: "Date signed",
  name: "Full name",
  text: "Text",
  checkbox: "Checkbox",
};

/** Size of a new field in PDF points (the server stores fractions of the page). */
export const DEFAULT_SIZES: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 160, h: 44 },
  initials: { w: 64, h: 44 },
  dateSigned: { w: 110, h: 20 },
  name: { w: 150, h: 20 },
  text: { w: 150, h: 20 },
  checkbox: { w: 16, h: 16 },
};

export const rColor = (i: number) => `var(--r${((i % 10) + 10) % 10})`;

export const EVENT_LABEL: Record<string, string> = {
  created: "Envelope created",
  document_uploaded: "Document uploaded",
  sent: "Sent for signature",
  email_sent: "Signing email sent",
  link_opened: "Opened the signing link",
  code_sent: "Verification code emailed",
  code_verified: "Verified with emailed code",
  account_verified: "Verified by signing in to eSignIt",
  code_failed: "Entered a wrong code",
  code_locked: "Locked out after wrong codes",
  consented: "Agreed to sign electronically",
  viewed: "Viewed the document",
  signed: "Signed",
  declined: "Declined to sign",
  reminded: "Reminder sent",
  recipient_corrected: "Email address corrected",
  voided: "Voided",
  expired: "Expired",
  completed: "Completed and sealed",
  final_emailed: "Signed copy emailed",
  final_downloaded: "Signed copy downloaded",
  bounced: "Email bounced",
  complained: "Email marked as spam",
  record_linked: "Linked to a CRM record",
  record_unlinked: "CRM record unlinked",
  delivery_step: "Sent to a connected system",
  delivery_done: "Connected system updated",
  delivery_failed: "Connected system update failed",
};

export const newId = (prefix: string) => `${prefix}-${crypto.randomUUID().slice(0, 13)}`;

export const ROLE_LABEL = { owner: "Owner", admin: "Admin", sender: "Sender" } as const;

/** What each connector does, for the admin console. */
export const CONNECTOR_INFO: Record<string, { blurb: string; color: string }> = {
  webhook: { blurb: "POST a signed JSON event to any HTTPS endpoint, with a link to the sealed PDF.", color: "#64748b" },
  salesforce: { blurb: "File the signed PDF on the linked record, set a field, post to Chatter.", color: "#00a1e0" },
  hubspot: { blurb: "Attach the signed PDF to the linked deal and set a property.", color: "#ff7a59" },
  dynamics: { blurb: "Add the signed PDF as a note on the linked Dataverse record.", color: "#0b53ce" },
  sharepoint: { blurb: "Save every signed PDF to a SharePoint document library.", color: "#038387" },
  s3: { blurb: "Write every signed PDF and its audit JSON to your own S3 bucket.", color: "#e47911" },
  infor: { blurb: "Store the signed PDF in Infor Document Management, then update a Landmark record.", color: "#c8102e" },
};
